import type { CSSProperties } from 'react'
import type { Particle } from './cipher-particles'

import { useEffect, useRef } from 'react'

import { MONO_FAMILY } from '@/lib/fonts'

import { PANELS } from './cipher-data'
import { CipherPanel } from './cipher-panel'
import { buildBtcFrame, buildGoldFrame, buildStatsFrame } from './cipher-frames'

import {
    cellMetrics,
    buildOutgoing,
    buildIncoming,
    buildEntrance,
    renderParticles,
    renderGrid,
    clamp01,
    smoothstep
} from './cipher-particles'

// --- Scroll choreography ----------------------------------------------------
const HOLD1_END = 0.03
const TRANS1_END = 0.36
const HOLD2_END = 0.55
const TRANS2_END = 0.85
const FLIP_START = 0.78
const FLIP_END = 0.86

// --- Animation tunables -----------------------------------------------------
const ENTRANCE_MS = 2000
const CURL_AMP_PX = 22
const SCORCH_AMP = 0.45
const ENTRANCE_CURL_AMP = CURL_AMP_PX * 2.4
const ENTRANCE_SCORCH_AMP = SCORCH_AMP * 1.3
const SLIDE = 80
const SECTION_VH = 520

// --- Palette ----------------------------------------------------------------
const BG_LIGHT = '#cff060'
const FG_LIGHT = '#1c5f27'
const BG_DARK = '#1c5f27'
const FG_DARK = '#cff060'

const TRANS_OPTS = { curlAmp: CURL_AMP_PX, scorchAmp: SCORCH_AMP }
const ENTRANCE_OPTS = { curlAmp: ENTRANCE_CURL_AMP, scorchAmp: ENTRANCE_SCORCH_AMP }

function hexToRgb(hex: string): [number, number, number] {
    const h = hex.replace('#', '')
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)]
}

function mixHex(a: string, b: string, t: number): string {
    const [ar, ag, ab] = hexToRgb(a)
    const [br, bg, bb] = hexToRgb(b)
    const r = Math.round(ar + (br - ar) * t)
    const g = Math.round(ag + (bg - ag) * t)
    const bl = Math.round(ab + (bb - ab) * t)
    return '#' + [r, g, bl].map(v => v.toString(16).padStart(2, '0')).join('')
}

function paletteAt(p: number): { fg: string; bg: string } {
    if (p <= FLIP_START) return { fg: FG_LIGHT, bg: BG_LIGHT }
    if (p >= FLIP_END) return { fg: FG_DARK, bg: BG_DARK }
    const t = smoothstep((p - FLIP_START) / (FLIP_END - FLIP_START))
    return { fg: mixHex(FG_LIGHT, FG_DARK, t), bg: mixHex(BG_LIGHT, BG_DARK, t) }
}

// The slide direction matches the successor panel's seat.
function panelState(i: number, p: number): { alpha: number; offset: number } {
    if (i === 0) {
        if (p < HOLD1_END) return { alpha: 1, offset: 0 }
        const t = smoothstep(clamp01((p - HOLD1_END) / 0.08))
        return { alpha: 1 - t, offset: -SLIDE * t }
    }

    if (i === 1) {
        if (p < TRANS1_END - 0.06) return { alpha: 0, offset: SLIDE }
        if (p < TRANS1_END + 0.04) {
            const t = smoothstep(clamp01((p - (TRANS1_END - 0.06)) / 0.1))
            return { alpha: t, offset: SLIDE * (1 - t) }
        }
        if (p < HOLD2_END) return { alpha: 1, offset: 0 }
        if (p < HOLD2_END + 0.08) {
            const t = smoothstep(clamp01((p - HOLD2_END) / 0.08))
            return { alpha: 1 - t, offset: SLIDE * t }
        }
        return { alpha: 0, offset: SLIDE }
    }

    if (p < TRANS2_END - 0.06) return { alpha: 0, offset: -SLIDE }
    if (p < TRANS2_END + 0.04) {
        const t = smoothstep(clamp01((p - (TRANS2_END - 0.06)) / 0.1))
        return { alpha: t, offset: -SLIDE * (1 - t) }
    }
    return { alpha: 1, offset: 0 }
}

export const CipherSection = () => {
    const containerRef = useRef<HTMLElement | null>(null)
    const rootRef = useRef<HTMLDivElement | null>(null)
    const bgRef = useRef<HTMLDivElement | null>(null)
    const canvasRef = useRef<HTMLCanvasElement | null>(null)
    const panelRefs = useRef<(HTMLDivElement | null)[]>([])
    const chipRef = useRef<HTMLDivElement | null>(null)
    const hintRef = useRef<HTMLDivElement | null>(null)
    const railTrackRef = useRef<HTMLDivElement | null>(null)
    const railFillRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        const canvas = canvasRef.current
        const container = containerRef.current
        const root = rootRef.current
        if (!canvas || !container || !root) return

        const ctx = canvas.getContext('2d')
        if (!ctx) return

        // Build the three screen grids once — pure data, no canvas needed.
        const grids = {
            btc: buildBtcFrame(),
            gold: buildGoldFrame(),
            stats: buildStatsFrame()
        }

        const layout = {
            vw: 0,
            vh: 0,
            cellW: 0,
            cellH: 0,
            canvasW: 0,
            canvasH: 0,
            mobile: false,
            frameY: 0,
            frameX1: 0,
            frameX2: 0,
            frameX3: 0
        }

        const particles = {
            entrance: [] as Particle[],
            t1out: [] as Particle[],
            t1in: [] as Particle[],
            t2out: [] as Particle[],
            t2in: [] as Particle[]
        }

        const S = {
            smoothP: 0,
            lastDrawnP: -1,
            entranceDone: false,
            entranceStart: null as number | null,
            entranceT: 0,
            fg: FG_LIGHT
        }

        const reduce = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
        if (reduce) {
            S.entranceDone = true
            S.entranceT = 1
        }

        function positionPanels() {
            const { vw, mobile, canvasW, canvasH, frameY } = layout

            if (mobile) {
                const top = frameY + canvasH + 20
                panelRefs.current.forEach(el => {
                    if (!el) return
                    el.style.left = '16px'
                    el.style.right = '16px'
                    el.style.width = 'auto'
                    el.style.top = `${top}px`
                    el.style.height = 'auto'
                    el.style.bottom = 'auto'
                })
                return
            }

            const sidePadding = Math.max(64, vw * 0.06)
            const containerW = Math.min(1440, vw - 2 * sidePadding)
            const containerX = (vw - containerW) / 2
            const artColW = Math.max(canvasW + 16, containerW * 0.28)
            const gap = 56
            const textColW = containerW - artColW - gap
            const textRightX = containerX + artColW + gap
            const textLeftX = containerX
            const seats = [textRightX, textLeftX, textRightX]

            seats.forEach((x, i) => {
                const el = panelRefs.current[i]
                if (!el) return
                el.style.left = `${x}px`
                el.style.right = 'auto'
                el.style.width = `${textColW}px`
                el.style.top = `${frameY}px`
                el.style.height = `${canvasH}px`
                el.style.bottom = 'auto'
            })
        }

        function computeLayout() {
            const vw = window.innerWidth
            const vh = window.innerHeight
            const m = cellMetrics(vw, vh)

            const dpr = Math.min(2.5, window.devicePixelRatio || 1)
            canvas.width = Math.round(vw * dpr)
            canvas.height = Math.round(vh * dpr)
            canvas.style.width = `${vw}px`
            canvas.style.height = `${vh}px`
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
            ctx.textBaseline = 'top'

            let frameY: number
            let frameX1: number
            let frameX2: number
            let frameX3: number

            if (m.mobile) {
                frameY = Math.max(40, vh * 0.06)
                const fx = (vw - m.canvasW) / 2
                frameX1 = fx
                frameX2 = fx
                frameX3 = fx
            } else {
                frameY = (vh - m.canvasH) / 2
                const sidePadding = Math.max(64, vw * 0.06)
                const containerW = Math.min(1440, vw - 2 * sidePadding)
                const containerX = (vw - containerW) / 2
                const artColW = Math.max(m.canvasW + 16, containerW * 0.28)
                frameX1 = containerX + (artColW - m.canvasW) / 2
                frameX2 = containerX + containerW - artColW + (artColW - m.canvasW) / 2
                frameX3 = frameX1
            }

            Object.assign(layout, {
                vw,
                vh,
                cellW: m.cellW,
                cellH: m.cellH,
                canvasW: m.canvasW,
                canvasH: m.canvasH,
                mobile: m.mobile,
                frameY,
                frameX1,
                frameX2,
                frameX3
            })

            positionPanels()

            const common = { cellW: m.cellW, cellH: m.cellH, viewportW: vw, viewportH: vh }
            particles.entrance = buildEntrance(grids.btc, { ...common, frameX: frameX1, frameY, rngSeed: 90210 })
            particles.t1out = buildOutgoing(grids.btc, { ...common, frameX: frameX1, frameY, windDir: 1, rngSeed: 1337 })
            particles.t1in = buildIncoming(grids.gold, { ...common, frameX: frameX2, frameY, windDir: 1, rngSeed: 2024 })
            particles.t2out = buildOutgoing(grids.gold, { ...common, frameX: frameX2, frameY, windDir: -1, rngSeed: 4242 })
            particles.t2in = buildIncoming(grids.stats, { ...common, frameX: frameX3, frameY, windDir: -1, rngSeed: 8675 })

            S.lastDrawnP = -1
        }

        function draw() {
            ctx.clearRect(0, 0, layout.vw, layout.vh)
            ctx.fillStyle = S.fg
            ctx.font = `500 ${layout.cellH * 0.92}px ${MONO_FAMILY}`
            ctx.textBaseline = 'top'

            if (!S.entranceDone) {
                renderParticles(ctx, particles.entrance, S.entranceT, ENTRANCE_OPTS)
                return
            }

            const p = S.smoothP

            if (p <= HOLD1_END) {
                renderGrid(ctx, grids.btc, layout.frameX1, layout.frameY, layout.cellW, layout.cellH)
            } else if (p <= TRANS1_END) {
                const local = (p - HOLD1_END) / (TRANS1_END - HOLD1_END)
                renderParticles(ctx, particles.t1out, local, TRANS_OPTS)
                renderParticles(ctx, particles.t1in, local, TRANS_OPTS)
            } else if (p <= HOLD2_END) {
                renderGrid(ctx, grids.gold, layout.frameX2, layout.frameY, layout.cellW, layout.cellH)
            } else if (p <= TRANS2_END) {
                const local = (p - HOLD2_END) / (TRANS2_END - HOLD2_END)
                renderParticles(ctx, particles.t2out, local, TRANS_OPTS)
                renderParticles(ctx, particles.t2in, local, TRANS_OPTS)
            } else {
                renderGrid(ctx, grids.stats, layout.frameX3, layout.frameY, layout.cellW, layout.cellH)
            }
        }

        function applyPalette(p: number) {
            const { fg, bg } = paletteAt(p)
            S.fg = fg
            const shadow = mixHex(fg, '#000000', 0.5)
            root!.style.setProperty('--cipher-fg', fg)
            root!.style.setProperty('--cipher-bg', bg)
            root!.style.setProperty('--cipher-shadow', shadow)
            if (bgRef.current) bgRef.current.style.background = bg
            document.documentElement.style.background = bg
        }

        function applyPanels(p: number, entranceFade: number) {
            for (let i = 0; i < 3; i++) {
                const el = panelRefs.current[i]
                if (!el) continue
                const { alpha, offset } = panelState(i, p)
                const a = alpha * entranceFade
                const off = layout.mobile ? 0 : offset
                el.style.opacity = String(a)
                el.style.transform = `translateX(${off}px)`
                el.style.filter = a < 0.999 ? `blur(${(1 - a) * 6}px)` : 'none'
                const active = a > 0.55
                el.style.pointerEvents = active ? 'auto' : 'none'
                el.style.zIndex = active ? '1000' : '0'
            }
        }

        function applyChrome(p: number, entranceFade: number) {
            if (layout.mobile) return
            if (chipRef.current) {
                chipRef.current.style.opacity = String(0.55 * entranceFade)
                const phase = p < 0.195 ? 1 : p < 0.7 ? 2 : 3
                chipRef.current.textContent = `cipher · 0${phase} / 03`
            }
            if (hintRef.current) hintRef.current.style.opacity = String(0.4 * entranceFade)
            if (railTrackRef.current) railTrackRef.current.style.opacity = String(0.18 * entranceFade)
            if (railFillRef.current) railFillRef.current.style.transform = `scaleY(${clamp01(p)})`
        }

        let raf = 0

        function tick(now: number) {
            if (!S.entranceDone) {
                if (S.entranceStart === null) S.entranceStart = now
                const t = Math.min(1, (now - S.entranceStart) / ENTRANCE_MS)
                S.entranceT = t
                const entranceFade = t < 0.5 ? 0 : (t - 0.5) / 0.5
                applyPalette(0)
                applyPanels(0, entranceFade)
                applyChrome(0, entranceFade)
                draw()
                if (t >= 1) {
                    // Hand off at the real scroll position so a user who scrolled
                    // during the entrance doesn't see the transitions replay.
                    S.entranceDone = true
                    const rect = container!.getBoundingClientRect()
                    const total = rect.height - window.innerHeight
                    S.smoothP = total > 0 ? clamp01(-rect.top / total) : 0
                }
                raf = requestAnimationFrame(tick)
                return
            }

            const rect = container!.getBoundingClientRect()
            const total = rect.height - window.innerHeight
            const targetP = total > 0 ? clamp01(-rect.top / total) : 0

            let sp = S.smoothP + (targetP - S.smoothP) * 0.12
            if (Math.abs(targetP - sp) < 0.0002) sp = targetP
            S.smoothP = sp

            if (Math.abs(sp - S.lastDrawnP) > 0.00005) {
                applyPalette(sp)
                applyPanels(sp, 1)
                applyChrome(sp, 1)
                draw()
                S.lastDrawnP = sp
            }

            raf = requestAnimationFrame(tick)
        }

        let resizeTimer = 0
        function onResize() {
            window.clearTimeout(resizeTimer)
            resizeTimer = window.setTimeout(computeLayout, 150)
        }

        let cancelled = false
        function begin() {
            if (cancelled) return
            computeLayout()
            applyPalette(0)
            applyPanels(0, 0)
            applyChrome(0, 0)
            raf = requestAnimationFrame(tick)
        }

        if (typeof document !== 'undefined' && document.fonts && document.fonts.ready) {
            const timeout = new Promise<void>(res => window.setTimeout(res, 800))
            Promise.race([document.fonts.ready, timeout]).then(begin).catch(begin)
        } else {
            begin()
        }

        window.addEventListener('resize', onResize)

        return () => {
            cancelled = true
            cancelAnimationFrame(raf)
            window.clearTimeout(resizeTimer)
            window.removeEventListener('resize', onResize)
            document.documentElement.style.background = ''
        }
    }, [])

    const rootStyle = {
        position: 'sticky',
        top: 0,
        height: '100vh',
        width: '100%',
        overflow: 'hidden',
        color: 'var(--cipher-fg)',
        '--cipher-fg': FG_LIGHT,
        '--cipher-bg': BG_LIGHT,
        '--cipher-shadow': mixHex(FG_LIGHT, '#000000', 0.5)
    } as CSSProperties

    return (
        <section
            ref={containerRef}
            aria-label='Cipher — a scroll-driven ASCII trading terminal'
            style={{
                position: 'relative',
                height: `${SECTION_VH}vh`,
                width: '100%',
                background: BG_LIGHT
            }}
        >
            <div ref={rootRef} style={rootStyle}>
                <div
                    ref={bgRef}
                    aria-hidden='true'
                    style={{ position: 'absolute', inset: 0, zIndex: 0, background: BG_LIGHT, pointerEvents: 'none' }}
                />

                <div
                    aria-hidden='true'
                    style={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 1,
                        background: 'radial-gradient(ellipse at center, transparent 35%, rgba(0,0,0,0.06) 95%)',
                        mixBlendMode: 'multiply',
                        pointerEvents: 'none'
                    }}
                />

                <canvas
                    ref={canvasRef}
                    aria-hidden='true'
                    style={{ position: 'absolute', inset: 0, zIndex: 2, pointerEvents: 'none' }}
                />

                {PANELS.map((panel, i) => (
                    <CipherPanel
                        key={panel.phase}
                        data={panel}
                        ref={el => {
                            panelRefs.current[i] = el
                        }}
                    />
                ))}

                <div
                    ref={chipRef}
                    aria-hidden='true'
                    className='hidden md:block'
                    style={{
                        position: 'absolute',
                        top: 24,
                        left: 24,
                        zIndex: 4,
                        opacity: 0,
                        fontFamily: MONO_FAMILY,
                        fontSize: 12,
                        textTransform: 'uppercase',
                        letterSpacing: '0.25em',
                        pointerEvents: 'none'
                    }}
                >
                    cipher · 01 / 03
                </div>

                <div
                    ref={hintRef}
                    aria-hidden='true'
                    className='hidden md:block'
                    style={{
                        position: 'absolute',
                        bottom: 24,
                        left: 24,
                        zIndex: 4,
                        opacity: 0,
                        fontFamily: MONO_FAMILY,
                        fontSize: 10,
                        textTransform: 'uppercase',
                        letterSpacing: '0.3em',
                        pointerEvents: 'none'
                    }}
                >
                    scroll · disintegrate · resolve
                </div>

                <div
                    ref={railTrackRef}
                    aria-hidden='true'
                    className='hidden md:block'
                    style={{
                        position: 'absolute',
                        left: 16,
                        top: '7vh',
                        bottom: '7vh',
                        width: 2,
                        zIndex: 4,
                        opacity: 0,
                        background: 'var(--cipher-fg)',
                        pointerEvents: 'none'
                    }}
                />

                <div
                    ref={railFillRef}
                    aria-hidden='true'
                    className='hidden md:block'
                    style={{
                        position: 'absolute',
                        left: 16,
                        top: '7vh',
                        height: '86vh',
                        width: 2,
                        zIndex: 4,
                        transformOrigin: 'top',
                        transform: 'scaleY(0)',
                        background: 'var(--cipher-fg)',
                        pointerEvents: 'none'
                    }}
                />
            </div>
        </section>
    )
}
